import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createAlert,
  modifyScheduledAlert,
  removeScheduledAlert,
  setCreateAlertStatus,
} from 'redux/actions';
import {
  LeftContainer,
  FHCard,
  FHCardContent,
  FHCardHeader,
  CardHeaderButton,
} from 'components/styled-components';
import { ConfirmDialog } from 'components/widgets';
import { CalendarIcon } from 'components/icons';
import { DatetimePicker } from 'components/widgets';
import { ExpSelect } from 'components/elements';
import {
  NewsForm,
  PromoForm,
  QuickPollForm,
  ScoreForm,
  GamedayThemeForm,
} from 'components/widgets';
import {
  ALERT_PROTO_TYPES,
  ALERT_LABELS,
  ALERT_FORM_MODES,
  LINKS,
} from 'utils/constants';
import { showWarningToast } from 'utils/helpers';
import moment from 'moment-timezone';

const Schedule = ({
  defaultValues = null,
  mode = ALERT_FORM_MODES.creating,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const createAlertStatus = useSelector((state) => state.notifications.createAlertStatus);

  const [type, setAlertType] = useState(
    defaultValues ? defaultValues.type : ALERT_PROTO_TYPES.news
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [datetime, setDatetime] = useState(
    defaultValues ? new Date(defaultValues.scheduledTime) : null
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleSubmit = useCallback(
    async (data) => {
      if (
        !datetime ||
        (datetime.getTime() - new Date().getTime()) / 1000 < 60 // less than one minute
      ) {
        showWarningToast(
          'Please set the scheduled time to be at least one minute after the current time!'
        );
        return;
      }

      if (mode === ALERT_FORM_MODES.updating) {
        dispatch(
          modifyScheduledAlert(defaultValues.id, {
            ...data,
            type,
            scheduledTime: datetime,
          })
        );
        navigate(LINKS.schedule.path);
      } else {
        dispatch(createAlert(type, data, datetime));
      }
    },
    [type, datetime, defaultValues, mode, dispatch, navigate]
  );

  const handleDelete = async () => {
    dispatch(removeScheduledAlert(defaultValues.id));
    navigate(LINKS.schedule.path);
  };

  useEffect(() => {
    dispatch(setCreateAlertStatus("idle"));
  }, [createAlertStatus, dispatch])

  const AlertForm = useMemo(() => {
    switch (type) {
      case ALERT_PROTO_TYPES.news:
        return NewsForm;
      case ALERT_PROTO_TYPES.survey:
        return QuickPollForm;
      case ALERT_PROTO_TYPES.score:
        return ScoreForm;
      case ALERT_PROTO_TYPES.promo:
        return PromoForm;
      case ALERT_PROTO_TYPES.gameday:
        return GamedayThemeForm;
      default:
        return NewsForm;
    }
  }, [type]);

  const handleDatetimeClick = useCallback(
    (e) => {
      if (!datetime) {
        setDatetime(new Date());
      }
      setAnchorEl(e.target);
    },
    [datetime]
  );

  return (
    <LeftContainer maxWidth="md">
      <FHCard>
        <FHCardHeader
          title={
            mode === ALERT_FORM_MODES.updating
              ? 'Update Scheduled Alert'
              : 'Create Scheduled Alert'
          }
          subheader={
            <CardHeaderButton
              startIcon={<CalendarIcon />}
              onClick={handleDatetimeClick}
              variant={datetime ? 'outlined' : 'contained'}
            >
              {datetime
                ? moment(datetime).format('MMM D @ hh:mm A')
                : 'Set date and time'}
            </CardHeaderButton>
          }
        />
        <FHCardContent>
          {mode === ALERT_FORM_MODES.creating && (
            <ExpSelect
              name="type"
              label="Alert type"
              items={Object.values(ALERT_PROTO_TYPES).map((type) => ({
                value: type,
                label: ALERT_LABELS[type],
              }))}
              value={type}
              onChange={(e) => setAlertType(e.target.value)}
            />
          )}
          <AlertForm
            onSubmit={handleSubmit}
            onDelete={() => setConfirmDialogOpen(true)}
            mode={mode}
            defaultValues={defaultValues}
            deleting={mode === ALERT_FORM_MODES.updating}
            isSuccess={createAlertStatus === "success"}
          />
          <DatetimePicker
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            value={datetime}
            onChange={setDatetime}
          />
          <ConfirmDialog
            open={confirmDialogOpen}
            title="Delete scheduled alert"
            contentText="Do you really want to delete this pending alert?"
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={handleDelete}
          />
        </FHCardContent>
      </FHCard>
    </LeftContainer>
  );
};

export default memo(Schedule);
